/**
 * Design Tokens — OKLCH CSS variable name constants.
 *
 * New chat components import these constants instead of writing raw
 * `var(--color-*)` strings or hardcoded Tailwind colour classes.
 * The semantic intent is captured once here so a future design-system
 * refactor changes one file rather than N components.
 *
 * The `classificationXxx` values are pre-composed Tailwind class
 * strings.  They encode the badge colour semantics for the three
 * classification tiers used by the ClassificationBanner and citation
 * status chips.  When a full token-to-class migration runs in Wave 3,
 * these will stay here as the canonical source and the scattered
 * raw-class usage in `chat-status-badges.tsx` / `chat-stream-state.ts`
 * will import from this file.
 *
 * ESLint rule (Wave 4): ban `text-amber-700`-style raw colour classes
 * outside of this file and migrated badge components.
 *
 * Spec: docs/CHAT-UI-IMPROVEMENT-DESIGN.md §2.4
 *
 * ── White-label theming additions (Wave 1.5) ────────────────────────────────
 *
 * `CSS_TOKEN` — map from semantic key to `'--color-*'` property name
 *   (as opposed to `TOKENS` which produces `'var(--color-*)'` CSS values).
 *   Use `CSS_TOKEN` when calling `style.setProperty()`.
 *
 * `BRANDABLE_TOKEN_TO_PROP` — subset of CSS_TOKEN that tenant branding
 *   may override. Platform-structural tokens (radius, success, warning,
 *   destructive) are intentionally excluded.
 *
 * `brandingColorsToCssProps(bc)` — converts a BrandingColors object to a
 *   Record<cssPropertyName, hex> ready for the hexToOklch conversion loop.
 *
 * `applyBrandingOverrides(branding, mode)` — convenience async wrapper
 *   that delegates to `applyBrandingToDocument` from
 *   `apply-tenant-branding.ts`.
 *
 * ## Dependency direction (no circular imports)
 *
 *   design-tokens  ──imports──▶  branding-defaults  (types only)
 *   apply-tenant-branding  ──imports──▶  design-tokens
 *   TenantBrandingProvider  ──imports──▶  apply-tenant-branding
 */

import type { BrandingColors, TenantBranding } from './branding-defaults';

// ── Semantic CSS variable references (Wave 2A) ────────────────────────────────

export const TOKENS = {
  // Core surface colours
  bg: 'var(--color-background)',
  fg: 'var(--color-foreground)',
  card: 'var(--color-card)',
  cardFg: 'var(--color-card-foreground)',
  popover: 'var(--color-popover)',
  popoverFg: 'var(--color-popover-foreground)',

  // Brand + interaction colours
  primary: 'var(--color-primary)',
  primaryFg: 'var(--color-primary-foreground)',
  primaryText: 'var(--color-primary-text)',
  secondary: 'var(--color-secondary)',
  secondaryFg: 'var(--color-secondary-foreground)',
  accent: 'var(--color-accent)',
  accentFg: 'var(--color-accent-foreground)',

  // Neutral text + surfaces
  muted: 'var(--color-muted)',
  mutedFg: 'var(--color-muted-foreground)',

  // Structural
  border: 'var(--color-border)',
  input: 'var(--color-input)',
  ring: 'var(--color-ring)',

  // Status colours
  destructive: 'var(--color-destructive)',
  destructiveFg: 'var(--color-destructive-foreground)',
  success: 'var(--color-success)',
  warning: 'var(--color-warning)',
  info: 'var(--color-info)',

  // Sidebar palette
  sidebar: 'var(--color-sidebar)',
  sidebarFg: 'var(--color-sidebar-foreground)',
  sidebarPrimary: 'var(--color-sidebar-primary)',
  sidebarPrimaryFg: 'var(--color-sidebar-primary-foreground)',
  sidebarAccent: 'var(--color-sidebar-accent)',
  sidebarAccentFg: 'var(--color-sidebar-accent-foreground)',
  sidebarBorder: 'var(--color-sidebar-border)',

  // ── Semantic chat tokens: pre-composed Tailwind class strings ──────────
  // These encode the three classification-tier badge palettes.
  // Import here instead of duplicating bg-blue-500/10 etc. across components.
  classificationCui:
    'border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-300',
  classificationItar:
    'border-amber-500/30 bg-amber-500/10 text-amber-800 dark:text-amber-300',
  classificationRestricted:
    'border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300',
} as const;

// ── Type helpers ─────────────────────────────────────────────────────────────

/** Union of all token keys — useful for strongly-typed prop patterns. */
export type TokenKey = keyof typeof TOKENS;

/** Union of all token values. */
export type TokenValue = (typeof TOKENS)[TokenKey];

// ── CSS property name constants (Wave 1.5 white-label theming) ───────────────
//
// `CSS_TOKEN` is the companion to `TOKENS`:
//   TOKENS.primary   → 'var(--color-primary)'    (use in CSS-in-JS / className)
//   CSS_TOKEN.primary → '--color-primary'         (use with style.setProperty())

export const CSS_TOKEN = {
  // Base palette
  background:              '--color-background',
  foreground:              '--color-foreground',
  card:                    '--color-card',
  cardForeground:          '--color-card-foreground',
  popover:                 '--color-popover',
  popoverForeground:       '--color-popover-foreground',
  primary:                 '--color-primary',
  primaryForeground:       '--color-primary-foreground',
  primaryText:             '--color-primary-text',
  secondary:               '--color-secondary',
  secondaryForeground:     '--color-secondary-foreground',
  muted:                   '--color-muted',
  mutedForeground:         '--color-muted-foreground',
  accent:                  '--color-accent',
  accentForeground:        '--color-accent-foreground',
  destructive:             '--color-destructive',
  destructiveForeground:   '--color-destructive-foreground',
  border:                  '--color-border',
  input:                   '--color-input',
  ring:                    '--color-ring',
  // Sidebar
  sidebar:                 '--color-sidebar',
  sidebarForeground:       '--color-sidebar-foreground',
  sidebarPrimary:          '--color-sidebar-primary',
  sidebarPrimaryForeground:'--color-sidebar-primary-foreground',
  sidebarAccent:           '--color-sidebar-accent',
  sidebarAccentForeground: '--color-sidebar-accent-foreground',
  sidebarBorder:           '--color-sidebar-border',
  sidebarRing:             '--color-sidebar-ring',
  // Semantic
  success: '--color-success',
  warning: '--color-warning',
  info:    '--color-info',
} as const satisfies Record<string, `--color-${string}`>;

export type CssTokenKey = keyof typeof CSS_TOKEN;

// ── Brandable token subset ────────────────────────────────────────────────────

/**
 * The subset of CSS custom properties that tenant branding may override.
 * Platform-structural tokens (radius, sidebar-width, success, warning,
 * destructive) are intentionally excluded — they are platform-locked.
 *
 * Used by `clearBrandingOverrides()` in `apply-tenant-branding.ts` to
 * know exactly which inline properties to remove on a reset.
 */
export const BRANDABLE_TOKEN_TO_PROP: Record<string, `--color-${string}`> = {
  primary:                  CSS_TOKEN.primary,
  primaryForeground:        CSS_TOKEN.primaryForeground,
  primaryText:              CSS_TOKEN.primaryText,
  background:               CSS_TOKEN.background,
  foreground:               CSS_TOKEN.foreground,
  card:                     CSS_TOKEN.card,
  cardForeground:           CSS_TOKEN.cardForeground,
  popover:                  CSS_TOKEN.popover,
  popoverForeground:        CSS_TOKEN.popoverForeground,
  sidebar:                  CSS_TOKEN.sidebar,
  sidebarForeground:        CSS_TOKEN.sidebarForeground,
  sidebarPrimary:           CSS_TOKEN.sidebarPrimary,
  sidebarPrimaryForeground: CSS_TOKEN.sidebarPrimaryForeground,
  sidebarAccent:            CSS_TOKEN.sidebarAccent,
  sidebarAccentForeground:  CSS_TOKEN.sidebarAccentForeground,
  ring:                     CSS_TOKEN.ring,
  info:                     CSS_TOKEN.info,
} as const;

// ── brandingColorsToCssProps ──────────────────────────────────────────────────

/**
 * Maps a `BrandingColors` flat object to a `Record<cssPropertyName, hex>`
 * suitable for the conversion loop in `applyBrandingToDocument`.
 *
 * Only properties with non-null, non-empty values are included in the
 * output — the caller can safely call `hexToOklch` on every value.
 */
export function brandingColorsToCssProps(bc: BrandingColors): Record<string, string> {
  const out: Record<string, string> = {};

  const add = (prop: `--color-${string}`, hex: string | undefined | null) => {
    if (hex) out[prop] = hex;
  };

  add(CSS_TOKEN.primary,                  bc.primary);
  add(CSS_TOKEN.primaryForeground,        bc.primaryForeground);
  // primary-text: same hex as primary for custom branding;
  // tenants can specialise via the branding editor if needed.
  add(CSS_TOKEN.primaryText,              bc.primary);
  add(CSS_TOKEN.background,              bc.background);
  add(CSS_TOKEN.foreground,              bc.text);
  add(CSS_TOKEN.card,                    bc.card);
  add(CSS_TOKEN.cardForeground,          bc.text);
  add(CSS_TOKEN.popover,                 bc.popover);
  add(CSS_TOKEN.popoverForeground,       bc.text);
  add(CSS_TOKEN.sidebar,                 bc.sidebarBackground);
  add(CSS_TOKEN.sidebarForeground,       bc.sidebarText);
  add(CSS_TOKEN.sidebarPrimary,          bc.sidebarActive);
  add(CSS_TOKEN.sidebarPrimaryForeground,bc.sidebarActiveText);
  add(CSS_TOKEN.sidebarAccent,           bc.sidebarHover);
  add(CSS_TOKEN.sidebarAccentForeground, bc.sidebarText);
  add(CSS_TOKEN.ring,                    bc.primary);
  add(CSS_TOKEN.info,                    bc.primary);

  return out;
}

// ── applyBrandingOverrides (convenience re-export) ────────────────────────────

/**
 * Convenience wrapper — delegates to `applyBrandingToDocument` from
 * `apply-tenant-branding.ts`.
 *
 * Prefer calling `applyBrandingToDocument` directly in application code;
 * this export exists so callers that only import `design-tokens` can
 * trigger a full apply without a second import.
 *
 * The import is done inside the function body to avoid introducing a
 * circular module dependency at the top level (apply-tenant-branding
 * imports from design-tokens; design-tokens must not import from
 * apply-tenant-branding at the module level).
 */
export async function applyBrandingOverrides(
  branding: TenantBranding | null,
  mode: 'light' | 'dark',
): Promise<void> {
  const { applyBrandingToDocument } = await import('./apply-tenant-branding');
  applyBrandingToDocument(branding, mode);
}
